import type { MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'

// Gallery2 wording for the shared menu-model entry vocabulary.
const labels: Record<DeskViewMode, string> = {
  msg: 'Gallery2',
  answers: 'Photos',
  submsges: 'Sections',
  attachments: 'Files',
  favs: 'Favorites',
  drafts: 'Drafts',
  trash: 'Trash',
}

export function Gallery2Menu({
  isEditorOpen,
  editorMode,
  settingsOpen,
  onToggleSettings,
}: {
  isEditorOpen: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
}) {
  const menu = useDeskMenu()
  const canAddAnswer = menu.can.reply
  const canAddMessage = menu.can.createMessage
  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  const locked = !menu.access.showUnlocked

  const saveLabel = editorMode === 'new_answer'
    ? 'Save Answer'
    : editorMode === 'new_submsg'
      ? 'Save Message'
      : 'Save'

  function navigate(event: MouseEvent<HTMLButtonElement>, mode: DeskViewMode) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  function save(event: MouseEvent<HTMLButtonElement>) {
    dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'gallery-menu:before-save' })
  }

  return (
    <>
    <nav
      {...legacyContractDataAttrs('sidebar.root')}
      className={`fixed inset-x-2 top-2 z-[100000] mx-auto flex h-11 max-w-[calc(100vw-16px)] items-center gap-1 overflow-x-auto rounded-xl border border-white/70 bg-white/90 p-1 text-xs text-slate-600 shadow-lg backdrop-blur-xl lg:max-w-max${locked ? ' hidden' : ''}`}
      style={locked ? { display: 'none' } : undefined}
      aria-label="Gallery2 controls"
    >
      <span className="flex h-9 flex-none items-center rounded-lg bg-slate-950 px-3 font-semibold text-white">gallery</span>
      {isEditorOpen ? (
        <>
          <button type="button" className="h-9 flex-none rounded-lg bg-violet-600 px-3 font-semibold text-white" onClick={save}>{saveLabel}</button>
          <button type="button" className="h-9 flex-none rounded-lg px-3 hover:bg-slate-100" aria-pressed={settingsOpen} onClick={onToggleSettings}>
            {settingsOpen ? 'Content' : 'Configure'}
          </button>
          <button type="button" className="h-9 flex-none rounded-lg px-3 text-rose-700 hover:bg-rose-50" onClick={event => navigate(event, 'msg')}>Close</button>
        </>
      ) : (
        <>
          {/* FAVS-IDENTITY: no Favorites tab for anonymous viewers of an edit-locked desk. */}
          {menu.tabs.filter(tab => tab.available && tab.hasItems && (!tab.requiresEdit || menu.access.editUnlocked) && (tab.mode !== 'favs' || menu.can.favs)).map(tab => (
            <button
              key={tab.mode}
              type="button"
              className={`h-9 flex-none rounded-lg px-3 font-medium ${tab.isActive ? 'bg-violet-100 text-violet-800' : 'hover:bg-slate-100 hover:text-slate-950'}`}
              aria-current={tab.isActive ? 'page' : undefined}
              onClick={event => navigate(event, tab.mode)}
            >
              {labels[tab.mode]}
            </button>
          ))}
          {canAddAnswer ? (
            <button
              type="button"
              className="h-9 flex-none rounded-lg bg-violet-50 px-3 font-semibold text-violet-700 hover:bg-violet-100"
              onClick={() => dispatchEditorAction({ action: 'open-reply' })}
            >
              + Photo
            </button>
          ) : null}
          {canAddMessage ? (
            <button
              type="button"
              className="h-9 flex-none rounded-lg bg-violet-50 px-3 font-semibold text-violet-700 hover:bg-violet-100"
              onClick={() => dispatchEditorAction({ action: 'open-new-message' })}
            >
              + Section
            </button>
          ) : null}
          <button type="button" className="h-9 flex-none rounded-lg bg-violet-600 px-3 font-semibold text-white" onClick={() => dispatchEditorAction({ action: 'open-edit-message' })}>Edit</button>
        </>
      )}
      <span className="ml-auto flex flex-none items-center gap-1">
        {/* POSSESSION-MODEL: desk state before the identity; identity keeps
            the bar's extreme right (= page top-right). */}
        <DeskStateBadge skin={{
          root: 'flex items-center whitespace-nowrap px-2 text-[11px] font-medium',
          possessedOpen: 'text-emerald-700',
          possessedPin: 'text-emerald-700',
          locked: 'text-slate-400',
          label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
          form: 'ml-1.5 inline-flex items-center gap-1',
          input: 'h-[26px] w-[110px] rounded-lg border border-slate-300 bg-white px-2 text-[12px] text-slate-900 outline-none',
          button: 'h-[26px] cursor-pointer rounded-lg px-2 text-[12px] text-violet-700 hover:bg-violet-50',
        }} />
        <AuthEntry skin={{
          root: 'flex items-center gap-1',
          signInButton: 'h-9 flex-none rounded-lg px-3 font-medium hover:bg-violet-50 hover:text-violet-700',
          identity: 'flex items-center gap-1',
          identityLink: 'font-semibold text-violet-700 hover:underline max-w-[110px] overflow-hidden text-ellipsis whitespace-nowrap',
          signOutButton: 'h-9 flex-none rounded-lg px-2 hover:bg-violet-50',
        }} />
      </span>
    </nav>
    {/* PARENTS-MENU: shared skeleton leaf, gallery skin. The bar is
        overflow-x-auto AND backdrop-blurred (its own fixed containing block),
        so no in-bar dropdown can escape it — the menu floats as a chip right
        under the bar, mirroring its centering; it renders nothing when the
        page has no parent chain. Hidden on a locked desk. */}
    {locked ? null : (
    <ParentsMenu skin={{
      root: 'fixed inset-x-2 top-[52px] z-[99999] mx-auto flex w-max max-w-[calc(100vw-16px)] flex-col items-start',
      trigger: 'flex h-7 cursor-pointer items-center gap-1 rounded-lg border border-white/70 bg-white/90 px-3 text-xs font-medium text-slate-600 shadow-lg backdrop-blur-xl hover:bg-violet-50 hover:text-violet-700',
      triggerOpen: 'bg-violet-100 text-violet-800',
      count: 'text-[10px] text-slate-400',
      list: 'mt-1 flex min-w-[200px] max-w-[90vw] flex-col gap-0.5 rounded-xl border border-white/70 bg-white/95 p-2 text-xs shadow-lg backdrop-blur-xl',
      link: 'truncate rounded-lg px-3 py-1.5 text-violet-700 no-underline hover:bg-violet-50 hover:underline',
      homeLink: 'mt-0.5 truncate rounded-lg border-t border-slate-200 px-3 py-1.5 pt-2 text-slate-500 no-underline hover:bg-slate-100',
    }} />
    )}
    </>
  )
}
